'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback } from 'react';
import { Modal, type ModalProps } from '@udemy-v2/react-dialog-components';
import { useI18n } from '@udemy-v2/i18n';
import { Button } from '@udemy-v2/react-core-components';
import { publishEvent, TrackImpression } from '@udemy-v2/event-tracking';
import classNames from 'classnames';
import { CodingExercisePromotionSeeDemoLinkClickEvent, CodingExercisePromotionDemoVideoImpressionEvent } from './events';
import { MediaPlayerWrapper } from '../media-player/asset-media-player.react-component';
import { usePreviewableAssetQuery, type AssetVideo } from '@/generated/graphql';
import styles from './coding-exercise-promotion-modal.module.scss';
interface CodingExercisePromotionModalProps {
  onClose: ModalProps['onClose'];
  isOpen: ModalProps['isOpen'];
  assetId: number;
  courseId: number;
  trackingId: string;
}
export const CodingExercisePromotionModal = t0 => {
  const $ = _c(22);
  const {
    onClose,
    isOpen,
    assetId,
    courseId,
    trackingId
  } = t0;
  const {
    gettext
  } = useI18n();
  const t1 = String(assetId);
  let t2;
  if ($[0] !== t1) {
    t2 = {
      assetId: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isOpen) {
    t3 = {
      enabled: isOpen
    };
    $[2] = isOpen;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data
  } = usePreviewableAssetQuery(t2, t3);
  const assetVideo = data?.asset?.__typename === "AssetVideo" ? data.asset as AssetVideo : undefined;
  let t4;
  if ($[4] !== courseId || $[5] !== trackingId) {
    t4 = () => {
      publishEvent(new CodingExercisePromotionDemoVideoImpressionEvent({
        id: courseId,
        trackingId
      }));
    };
    $[4] = courseId;
    $[5] = trackingId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const trackImpression = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classNames("ud-heading-lg", styles["demo-intro"]);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== gettext) {
    t6 = gettext("Coding Exercises Demo");
    $[8] = gettext;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <h2 id="coding-exercise-promotion-modal-title"><span className={t5}>{t6}</span></h2>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== assetVideo) {
    t8 = <div className={styles["video-container"]}><MediaPlayerWrapper asset={assetVideo} /></div>;
    $[12] = assetVideo;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8 || $[15] !== trackImpression) {
    t9 = <TrackImpression trackFunc={trackImpression} visibilityThreshold={0.25}>{t8}</TrackImpression>;
    $[14] = t8;
    $[15] = trackImpression;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isOpen || $[18] !== onClose || $[19] !== t7 || $[20] !== t9) {
    t10 = <Modal className="ud-color-scheme-dark" renderTitle={_temp} title="" isOpen={isOpen} onClose={onClose}>{t7}{t9}</Modal>;
    $[17] = isOpen;
    $[18] = onClose;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};
interface CodingExerciseModalTriggerProps {
  assetId: number;
  courseId: number;
  ctaText: string;
  trackingId: string;
}
export const CodingExerciseModalTrigger = t0 => {
  const $ = _c(15);
  const {
    assetId,
    courseId,
    ctaText,
    trackingId
  } = t0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t1;
  if ($[0] !== courseId || $[1] !== trackingId) {
    const trackCodingExerciseSeeDemoLinkClick = () => {
      publishEvent(new CodingExercisePromotionSeeDemoLinkClickEvent({
        id: courseId,
        trackingId
      }));
    };
    t1 = () => {
      trackCodingExerciseSeeDemoLinkClick();
      setIsModalOpen(true);
    };
    $[0] = courseId;
    $[1] = trackingId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleOpenModal = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsModalOpen(false);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleCloseModal = t2;
  let t3;
  if ($[4] !== ctaText || $[5] !== handleOpenModal) {
    t3 = <Button componentClass="a" className={styles["coding-exercise-modal-trigger"]} udStyle="link-underline" typography="ud-text-sm" onClick={handleOpenModal}>{ctaText}</Button>;
    $[4] = ctaText;
    $[5] = handleOpenModal;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== assetId || $[8] !== courseId || $[9] !== isModalOpen || $[10] !== trackingId) {
    t4 = <CodingExercisePromotionModal isOpen={isModalOpen} onClose={handleCloseModal} assetId={assetId} courseId={courseId} trackingId={trackingId} />;
    $[7] = assetId;
    $[8] = courseId;
    $[9] = isModalOpen;
    $[10] = trackingId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
function _temp() {
  return ["title-in-body-modal-title", null];
}